import React, { useEffect, useRef } from 'react';
import { Row, Col } from 'antd/es/grid';
import { useAppDispatch, useAppSelector } from 'redux/store';
import { TIconNames } from 'components/Icon';
import { changeHeaderCollapsedState, setHeaderHeight } from 'redux/slices';
import useElementResize from 'hooks/useElementResize';
import useMediaQuery from 'hooks/useMediaQuery';
import { SSidebarExpandIcon } from 'components/Sidebar/Sidebar.style';
import SHeader from './Header.style';
import SidebarSwitcher from '../SidebarSwitcher/SidebarSwitcher';
import ProfileMenu from '../ProfileMenu/ProfileMenu';
import CreateButton from '../CreateButton/CreateButton';
import Gauges from '../Gauges/Gauges';
import GaugeMobileMenu from '../Gauges/components/GaugeMobileMenu';
import TimeTracker from '../TimeTracker/TimeTracker';
import ThemeToggle from '../ThemeToggle/ThemeToggle';
import HeaderAlert from '../HeaderAlert/HeaderAlert';
import Help from '../../Help/Help';

const Header = () => {
  const dispatch = useAppDispatch();
  const isHeaderCollapsed = useAppSelector(
    state => state.appSettings.header.collapsed,
  );
  const isAccessLimited = useAppSelector(
    state => state.paymentInfoSlice?.isAccessLimited,
  );
  // ZEST-735: the Dashbar has three responsive states, so gauges stay visible
  // as long as they reasonably fit:
  //  - >=1600px  full labelled gauges on one auto-fitting row (respects the
  //              user's expand/collapse chevron);
  //  - 992-1599  the labelled row no longer fits, so show the compact
  //              icon-only row (still inline, still one glance);
  //  - <992px    true mobile: fold into the three-dot dropdown menu.
  // 992px intentionally matches antd's `lg` breakpoint (the grid the rest of the
  // header uses). 1600 replaces ZEST-735's 1280: the ZEST-757 tiles carry a
  // "Label: value" line per metric, so the labelled row needs ~909px of gauge
  // space — at 1366 it had 712 and squeezed tiles until headings truncated and
  // neighbouring values collided.
  const isDesktop = useMediaQuery('(min-width: 1600px)');
  const isTabletUp = useMediaQuery('(min-width: 992px)');
  const headerRef = useRef<HTMLDivElement>(null);
  const { height } = useElementResize({ ref: headerRef });

  useEffect(() => {
    dispatch(setHeaderHeight(height));
  }, [height]);

  if (isAccessLimited) {
    return (
      <div ref={headerRef}>
        <SHeader className="header">
          <Row justify="end" align="top">
            <ProfileMenu isAccessLimited />
          </Row>
        </SHeader>
      </div>
    );
  }
  return (
    <div ref={headerRef}>
      <HeaderAlert />
      <SHeader className="header">
        <Row justify="space-between" align="top" wrap={false}>
          <Col md={2} lg={0} className="mt-2">
            <SidebarSwitcher />
          </Col>
          {isTabletUp && (
            <Col xl={24} xxl={24} lg={24} flex="6 1 6px">
              <Row wrap={false} align="top">
                {isDesktop && (
                  <Col>
                    <SSidebarExpandIcon
                      size={22}
                      icon={
                        isHeaderCollapsed
                          ? TIconNames.EXPAND_RIGHT
                          : TIconNames.COLLAPSE_LEFT
                      }
                      onClick={() =>
                        dispatch(changeHeaderCollapsedState(!isHeaderCollapsed))
                      }
                      className="pointer mt-1 header-expand-icon"
                    />
                  </Col>
                )}
                <Gauges fitOneRow forceCollapsed={!isDesktop} />
              </Row>
            </Col>
          )}
          <Col flex="0 1 370px">
            <Row justify="end" align="middle" wrap={false}>
              <Col offset={1} id="dashbar_time_tracker">
                <TimeTracker />
              </Col>
              <Col offset={1} id="dashbar_theme_toggle">
                <ThemeToggle />
              </Col>
              <Col offset={1} id="dashbar_create_button">
                <CreateButton />
              </Col>
              {/* <Col offset={1}>
              <Button
                width={32}
                type="primary"
                shape="circle"
                icon={<Icon icon={TIconNames.NOTIFICATION} size={15} />}
              />
            </Col> */}
              <Col offset={1}>
                <Help />
              </Col>
              <Col offset={1} id="dashbar_profile">
                <ProfileMenu />
              </Col>
              {!isTabletUp && (
                <Col offset={1}>
                  <GaugeMobileMenu />
                </Col>
              )}
            </Row>
          </Col>
        </Row>
      </SHeader>
    </div>
  );
};

export default Header;
